import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, Modal, TextField } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type ReverseBooksOnlyDialogProps = {
    isOpen: boolean;
    isPending: boolean;
    /** i18n key naming the origin ("Sale"), for the warning copy. */
    sourceLabelKey: string | null;
    reason: string;
    onReasonChange: (value: string) => void;
    canConfirm: boolean;
    onConfirm: () => void;
    onClose: () => void;
};

/**
 * Confirmation for undoing the books of an entry whose document still stands.
 *
 * Render-only. This is the deliberate half of bos-api BOS-135: the ordinary
 * remedy is to undo the sale, invoice or payment, and the drawer leads with
 * that. Someone who chooses this instead is knowingly leaving the record and
 * the ledger describing different things, so the dialog says so and takes a
 * reason — which the server records on the reversal, for whoever reads the
 * ledger next and wonders.
 */
export const ReverseBooksOnlyDialog: React.FC<ReverseBooksOnlyDialogProps> = ({
    isOpen,
    isPending,
    sourceLabelKey,
    reason,
    onReasonChange,
    canConfirm,
    onConfirm,
    onClose,
}) => {
    const { t } = useTranslation('pages');
    // An origin this build has no label for still reaches here, via the
    // server's 409 (BOS-142). "a document" is the honest generic.
    const source = sourceLabelKey
        ? t(sourceLabelKey)
        : t('journal.drawer.reverseBooksGenericSource');

    return (
        <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-lg"
                        {...getNodeTestIdProp('journal-reverse-books-dialog')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading
                                {...getNodeTestIdProp(
                                    'journal-reverse-books-title',
                                )}
                            >
                                {t('journal.drawer.reverseBooksTitle')}
                            </Modal.Heading>
                        </Modal.Header>
                        <Modal.Body className="flex flex-col gap-4 pb-2">
                            <div className="flex items-start gap-2 rounded-xl bg-danger-soft p-4">
                                <Icon
                                    aria-hidden
                                    className="mt-0.5 size-4 shrink-0 text-danger-soft-foreground"
                                    icon="gravity-ui:circle-exclamation"
                                />
                                <p
                                    className="text-sm text-danger-soft-foreground"
                                    {...getNodeTestIdProp(
                                        'journal-reverse-books-warning',
                                    )}
                                >
                                    {t('journal.drawer.reverseBooksWarning', {
                                        source,
                                    })}
                                </p>
                            </div>
                            <TextField
                                fullWidth
                                isRequired
                                onChange={onReasonChange}
                                value={reason}
                            >
                                <Label>
                                    {t('journal.drawer.reverseReasonLabel')}
                                </Label>
                                <Input
                                    placeholder={t(
                                        'journal.drawer.reverseReasonPlaceholder',
                                    )}
                                    {...getNodeTestIdProp(
                                        'journal-reverse-books-reason',
                                    )}
                                />
                            </TextField>
                            <p className="text-xs text-muted">
                                {t('journal.drawer.reverseReasonHelper')}
                            </p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                onPress={onClose}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'journal-reverse-books-cancel',
                                )}
                            >
                                {t('journal.drawer.reverseBooksCancel')}
                            </Button>
                            <Button
                                isDisabled={!canConfirm}
                                isPending={isPending}
                                onPress={onConfirm}
                                variant="danger"
                                {...getNodeTestIdProp(
                                    'journal-reverse-books-submit',
                                )}
                            >
                                {t('journal.drawer.reverseBooksSubmit')}
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
